import React, { useEffect } from 'react';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import { SEOHero, SEOSection, FeatureGrid, BenefitList, InternalLinkGrid, CTABanner } from '../../components/SEOPageComponents';
import { Laptop, Receipt, Shield, Zap, Plug, Clock, RefreshCw } from 'lucide-react';

export default function PmsEInvoiceIntegration() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SEO
        title="PMS e-Invoice Integration — Connect Your Hotel PMS to LHDN"
        description="StayMii integrates with your hotel PMS (Beds24, Guesty, Mews) to automate LHDN e-invoice generation. No system migration required."
        keywords="pms e invoice integration, pms e invoice, hotel pms e invoice, beds24 e invoice, guesty e invoice, property management system e invoice, pms lhdn integration"
        canonicalUrl="/pms-e-invoice-integration"
      />
      <div className="min-h-screen bg-white">
        <LandingNavBar />
        <SEOHero
          badge="PMS Integration"
          h1="PMS e-Invoice Integration: Connect Your Hotel PMS to LHDN MyInvois"
          subtitle="StayMii plugs into your existing property management system — Beds24, Little Hotelier, Guesty, or Mews — and automates LHDN-compliant e-invoicing. No migration, no disruption."
        />

        <SEOSection h2="Supported PMS Integrations" description="StayMii is designed to work with any PMS that provides API access. Current and upcoming integrations:">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: 'Beds24', status: 'Officially Live – 23 October 2026', desc: 'Full booking sync, rate management, and automated e-invoice generation from Beds24 data.' },
              { name: 'Little Hotelier', status: 'Coming Soon', desc: 'Small hotel PMS integration with automated invoicing for direct and OTA bookings.' },
              { name: 'Guesty', status: 'Coming Soon', desc: 'Short-term rental PMS with multi-property e-invoice support for Airbnb and vacation rental operators.' },
              { name: 'Mews', status: 'Coming Soon', desc: 'Modern hotel PMS integration with real-time booking sync and e-invoice automation.' },
              { name: 'Your PMS', status: 'Custom', desc: 'Have a different PMS? Contact us. StayMii can integrate with any system that provides API access.' },
            ].map((pms, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition-all">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-slate-900">{pms.name}</h3>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${pms.status === 'Custom' ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-[#2563EB]'}`}>{pms.status}</span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{pms.desc}</p>
              </div>
            ))}
          </div>
        </SEOSection>

        <SEOSection h2="How PMS e-Invoice Integration Works" bg="bg-slate-50">
          <FeatureGrid items={[
            { icon: Plug, title: 'Connect Your PMS', desc: 'StayMii connects to your PMS via API. No data migration, no system change. Your team continues using the same PMS.' },
            { icon: RefreshCw, title: 'Auto-Sync Bookings', desc: 'Bookings, check-ins, check-outs, and payments are synced automatically from your PMS to StayMii.' },
            { icon: Receipt, title: 'Generate e-Invoices', desc: 'Every booking and transaction is converted into a validated LHDN-compliant e-invoice in real-time.' },
            { icon: Shield, title: 'Submit to LHDN', desc: 'e-Invoices are submitted directly to LHDN MyInvois API with status tracking and error handling.' },
            { icon: Zap, title: 'Zero Manual Work', desc: 'The entire process is automated. No manual data entry, no copy-pasting between systems.' },
            { icon: Clock, title: 'Live in Days', desc: 'Most PMS integrations go live within 3-5 business days with our setup assistance.' },
          ]} />
        </SEOSection>

        <SEOSection h2="Related Solutions">
          <InternalLinkGrid links={[
            { to: '/hotel-e-invoice-system', title: 'Hotel e-Invoice System', desc: 'Complete hotel e-invoicing' },
            { to: '/channel-manager-e-invoice', title: 'Channel Manager e-Invoice', desc: 'OTA booking automation' },
            { to: '/e-invoice-malaysia', title: 'e-Invoice Malaysia', desc: 'Malaysia e-invoicing guide' },
            { to: '/lhdn-e-invoice', title: 'LHDN e-Invoice', desc: 'Compliance requirements' },
            { to: '/myinvois-api', title: 'MyInvois API', desc: 'API integration details' },
            { to: '/airbnb-e-invoice-malaysia', title: 'Airbnb e-Invoice', desc: 'Short-term rental compliance' },
          ]} />
        </SEOSection>

        <CTABanner title="Connect Your PMS to LHDN Today" subtitle="Automate e-invoicing from your existing PMS. No migration required." />
        <LandingFooter />
      </div>
    </>
  );
}
